import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MockImage } from './MockImage';
import DownloadButton from './DownloadButton';
import MockSaveButton from './MockSaveButton';
import { getMockImageUrl } from '../utils';
import { useSavedContent } from '../context/savedContent';
import { ImageDimensions, MockDimensions } from '../types';

interface Mock {
  id: string;
  uuid: string;
  description?: string;
  tags?: [string];
  palette?: [string];
}

interface MockPreviewProps {
  mock: Mock;
  asfId: string;
  imageId: number;
  onSelect: (mock: Mock) => void;
  imageDimensions: ImageDimensions;
  mockDimensions: MockDimensions;
  containerStyle?: React.CSSProperties;
  containerClassName?: string;
  showPalette?: boolean;
  showDownloadButton?: boolean;
  isSaved?: boolean;
  onSaveChange?: (isSaved: boolean) => void;
}

interface PaletteBarProps {
  palette?: string[];
  className?: string;
}

const PaletteBar = React.memo(
  ({
    palette,
    className = 'absolute bottom-0 left-1/2 transform -translate-x-1/2 h-8 flex rounded-b-lg overflow-hidden translate-y-8 group-hover:translate-y-0 transition-transform duration-300 w-full',
  }: PaletteBarProps) => {
    if (!palette?.length) return null;

    return (
      <div className={className}>
        {palette.map((color: string, index: number) => (
          <div key={index} className="flex-1" style={{ backgroundColor: color }} />
        ))}
      </div>
    );
  }
);

function MockPreview({
  mock,
  asfId,
  imageId,
  onSelect,
  imageDimensions,
  mockDimensions,
  containerStyle,
  containerClassName = '',
  showPalette = false,
  showDownloadButton = false,
  onSaveChange,
}: MockPreviewProps) {
  const { t: tc } = useTranslation('common');
  const { mockSaves } = useSavedContent();

  const isSaved = useMemo(() => {
    return mockSaves.some((save) => save.imageId === imageId.toString() && save.mockId === mock.id);
  }, [mockSaves, imageId, mock.id]);

  return (
    <div
      style={containerStyle}
      className={`relative aspect-square bg-gray-50 rounded-lg group hover:bg-gray-100 overflow-hidden cursor-pointer ${containerClassName}`}
      onClick={() => onSelect(mock)}
    >
      <MockImage
        mock={mock}
        asfId={asfId}
        imageId={imageId}
        width={300}
        height={300}
        imageDimensions={imageDimensions}
        mockDimensions={mockDimensions}
        className="w-full h-full"
      />

      {showPalette && mock.palette && (
        <div className="hidden sm:block">
          <PaletteBar palette={mock.palette} />
        </div>
      )}

      <div
        className="absolute top-0 right-0 p-4 z-20 flex flex-row items-center space-x-2 transition-opacity duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {showDownloadButton && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <DownloadButton
              imageUrl={getMockImageUrl(asfId, mock.uuid, imageId, imageDimensions, mockDimensions)}
              title={tc('download')}
              mockupId={mock.uuid}
              imageId={String(imageId)}
            />
          </div>
        )}

        <div
          className={`transition-opacity duration-200 ${isSaved ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        >
          <MockSaveButton
            mockId={mock.id}
            imageId={imageId}
            variant="ghost"
            isSaved={isSaved}
            onSaveChange={onSaveChange}
          />
        </div>
      </div>
    </div>
  );
}

export default MockPreview;
